import React from 'react'
import { GetStaticPaths, InferGetStaticPropsType } from 'next'

import { REVALIDATION_PERIODS } from '@scentregroup/shared/constants'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { fetchNationalStaticData } from '@scentregroup/shared/hub-components/universal-header'
import { getDefaultNationalMetaData } from '@scentregroup/shared/helpers/get-default-national-metadata'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { GetStaticProps, GetStaticPropsContext } from '../../types/next'
import { makeApolloClient } from '../../lib/apolloClient'
import { getNationalExperiencePage } from '../../lib/page-templates/fetching'
import { Template } from '../../components/page-templates/template'

export const getStaticPaths: GetStaticPaths = async () => ({
  // TODO: Generate popular paths?
  paths: process.env.SKIP_STATIC_PATHS ? [] : [],
  fallback: 'blocking',
})

export const getStaticProps: GetStaticProps = async ({
  locale,
  params = {},
}: GetStaticPropsContext) => {
  const client = makeApolloClient()
  const country = localeToCountry(locale)
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug

  const [nationalHeaderData, template] = await Promise.all([
    fetchNationalStaticData(client, { country }),
    getNationalExperiencePage(client, country, slug),
  ])

  if (!nationalHeaderData || !template) {
    return {
      notFound: true,
      revalidate: REVALIDATION_PERIODS.notFound,
    }
  }

  return {
    props: {
      country,
      nationalHeaderData,
      template,
    },
    revalidate: REVALIDATION_PERIODS.static,
  }
}

const NationalExperiencePage = (
  props: InferGetStaticPropsType<typeof getStaticProps>
) => {
  const { country, template, nationalHeaderData } = props
  const { title, shortDescription: description } = template
  const canonical = useGetHrefContext()()
  const defaultMetaData = getDefaultNationalMetaData({
    country,
    type: 'experience',
    category: title,
  })

  const metaData = {
    title: `Westfield ${country} | ${title}`,
    description: description || defaultMetaData.description,
    canonical,
  }

  return (
    <Layout
      country={country}
      centre={undefined}
      header={{ variant: 'direct', data: nationalHeaderData }}
      metaData={metaData}
      pageTitle={template.title}
    >
      <Template template={template} />
    </Layout>
  )
}

export default NationalExperiencePage
